import React, { useState, useEffect, useRef, useMemo, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiArrowRight,
  FiClock,
  FiCheck,
  FiX,
  FiFlag,
  FiPause,
  FiPlay,
  FiHome,
  FiRefreshCw,
  FiTarget,
  FiAward,
  FiTrendingUp,
  FiBookOpen,
  FiAlertCircle,
} from 'react-icons/fi';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import {
  Mafs,
  Coordinates,
  Plot,
  Point,
  Text,
  Polygon,
  Circle,
  Line,
  Vector,
} from 'mafs';
import 'mafs/core.css';
import {
  savePracticeCompletion,
  savePracticeSessionHistory,
  clearReadinessCache,
  syncSeenQuestionIds,
} from './api/quantPracticeAPI';
import {
  loadMultipleTopics,
  getQuestionsFromTopics,
} from './api/topicExercisesAPI';
import {
  QC_OPTIONS,
  normalizeNumericEntry,
  isGreQuantAnswerCorrect,
  isGreQuantQuestionAnswered,
} from './quantAnswerUtils';
import {
  loadSessionHistory,
  upsertSessionInHistory,
  getCombinedSeenQuestionIds,
  extractAnsweredQuestionIds,
  mergeLocalSeenQuestionIds,
} from './quantPracticeHistoryUtils';
import { redirectToLoginWithReturn } from '../../../utils/utils';
import { getStoredUser } from '../../../utils/authSession';
import { renderGreMathText as renderMathText } from '../../../utils/renderGreMath';
import './QuantPracticeSession.css';

// Error boundary for Mafs / chart rendering – catches runtime errors and shows
// a graceful fallback instead of breaking the entire question view.
class ChartErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.warn('Chart rendering error:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="qps-image-placeholder">
          [Chart could not be rendered – please report this question]
        </div>
      );
    }
    return this.props.children;
  }
}

const DEFAULT_QUESTION_COUNT = 12;
const DEFAULT_TOTAL_TIME_MINUTES = 21;
const DEFAULT_TIME_PER_QUESTION_SECONDS = 105;
const ACTIVE_SESSION_STORAGE_KEY = 'quantPracticeActiveSession';
const GRE_QUANT_SECTION_1 = {
  questions: 12,
  totalTimeSeconds: 21 * 60,
};
const GRE_QUANT_SECTION_2 = {
  questions: 15,
  totalTimeSeconds: 26 * 60,
};

const getOptionLetter = (idx) =>
  typeof idx === 'number' && idx >= 0 ? String.fromCharCode(65 + idx) : '—';

const compileFnExpr = (expr) => {
  if (typeof expr !== 'string') return null;
  const s = expr.trim();
  const allowed = /^[0-9xX+\-*/().,\s^πpiqrtasnmco]+$/;
  if (!allowed.test(s)) return null;

  let js = s
    .replace(/π/g, 'Math.PI')
    .replace(/\bpi\b/gi, 'Math.PI')
    .replace(/sqrt\(/gi, 'Math.sqrt(')
    .replace(/\^/g, '**')
    .replace(/\bx\b/gi, 'x');

  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function('x', `return (${js});`);
    fn(0);
    return fn;
  } catch {
    return null;
  }
};

const renderMafsImage = (imageComponent) => {
  if (!imageComponent || imageComponent.type !== 'mafs') return null;
  const props = imageComponent.props || {};
  const viewBox = props.viewBox || { x: [-5, 5], y: [-5, 5] };
  const height = props.height || 260;
  const width = props.width || 520;
  const elements = Array.isArray(props.elements) ? props.elements : [];

  // Check if coordinates element exists in elements
  const hasCoordinates = elements.some((el) => el?.type === 'coordinates');
  // Check if we should show axes (default true unless explicitly disabled)
  const showAxes = props.showAxes !== false;

  return (
    <div className="qps-mafs-wrap">
      <Mafs width={width} height={height} viewBox={viewBox} pan={false}>
        {/* Always render coordinates if showAxes is true and no explicit coordinates element */}
        {showAxes && !hasCoordinates && (
          <Coordinates.Cartesian
            xAxis={{
              lines: 1,
              labels: (n) => (Number.isInteger(n) ? n : ''),
            }}
            yAxis={{
              lines: 1,
              labels: (n) => (Number.isInteger(n) ? n : ''),
            }}
          />
        )}
        {elements.map((el, idx) => {
          if (!el || typeof el !== 'object') return null;
          const t = el.type;
          const p = el.props || {};

          if (t === 'coordinates') {
            // Enhanced coordinates with better defaults
            return (
              <Coordinates.Cartesian
                key={idx}
                xAxis={{
                  lines: p.xAxis?.lines ?? 1,
                  labels:
                    p.xAxis?.labels ?? ((n) => (Number.isInteger(n) ? n : '')),
                  ...p.xAxis,
                }}
                yAxis={{
                  lines: p.yAxis?.lines ?? 1,
                  labels:
                    p.yAxis?.labels ?? ((n) => (Number.isInteger(n) ? n : '')),
                  ...p.yAxis,
                }}
              />
            );
          }

          if (t === 'function') {
            const fn = compileFnExpr(p.fn);
            const domain = p.domain || viewBox.x;
            if (!fn) return null;
            return (
              <Plot.OfX
                key={idx}
                y={(x) => fn(x)}
                color={p.color || '#2563eb'}
                strokeWidth={p.strokeWidth || 2}
                domain={domain}
              />
            );
          }

          if (t === 'line') {
            const slope = Number(p.slope);
            const intercept = Number(p.intercept);
            const domain = p.domain || viewBox.x;
            if (!Number.isFinite(slope) || !Number.isFinite(intercept))
              return null;
            // If domain start === end, render as a vertical segment instead
            if (Array.isArray(domain) && domain[0] === domain[1]) {
              const xVal = domain[0];
              const yVal = slope * xVal + intercept;
              return (
                <Line.Segment
                  key={idx}
                  point1={[xVal, yVal - 0.5]}
                  point2={[xVal, yVal + 0.5]}
                  color={p.color || '#dc2626'}
                  weight={p.strokeWidth || 2}
                />
              );
            }
            return (
              <Plot.OfX
                key={idx}
                y={(x) => slope * x + intercept}
                color={p.color || '#dc2626'}
                strokeWidth={p.strokeWidth || 2}
                domain={domain}
              />
            );
          }

          if (t === 'point') {
            return (
              <Point
                key={idx}
                x={Number(p.x)}
                y={Number(p.y)}
                color={p.color || '#64748b'}
              />
            );
          }

          if (t === 'text') {
            // Mafs Text only supports: x, y, color, size, attach, attachDistance, svgTextProps
            const svgTextProps = {};
            if (p.weight) svgTextProps.fontWeight = p.weight;
            if (p.angle) svgTextProps.transform = `rotate(${p.angle})`;
            return (
              <Text
                key={idx}
                x={Number(p.x)}
                y={Number(p.y)}
                color={p.color || '#94a3b8'}
                size={p.size || 14}
                svgTextProps={
                  Object.keys(svgTextProps).length > 0
                    ? svgTextProps
                    : undefined
                }
              >
                {p.text || ''}
              </Text>
            );
          }

          if (t === 'polygon') {
            const pts = Array.isArray(p.points) ? p.points : [];
            return (
              <Polygon
                key={idx}
                points={pts}
                color={p.fill || p.color || '#93c5fd'}
                fillOpacity={p.opacity ?? 0.3}
              />
            );
          }

          if (t === 'circle') {
            const center = Array.isArray(p.center) ? p.center : [0, 0];
            const radius = Number(p.radius);
            if (!Number.isFinite(radius)) return null;
            return (
              <Circle
                key={idx}
                center={center}
                radius={radius}
                color={p.color || '#2563eb'}
              />
            );
          }

          // Number line - horizontal line with labeled points
          if (t === 'numberline') {
            const xRange = p.range || viewBox.x;
            const yPos = p.y ?? 0;
            const points = Array.isArray(p.points) ? p.points : [];
            return (
              <React.Fragment key={idx}>
                {/* Main horizontal line */}
                <Line.Segment
                  point1={[xRange[0], yPos]}
                  point2={[xRange[1], yPos]}
                  color={p.color || '#94a3b8'}
                  weight={p.weight || 2}
                />
                {/* Arrow heads */}
                <Line.Segment
                  point1={[xRange[0] + 0.2, yPos + 0.15]}
                  point2={[xRange[0], yPos]}
                  color={p.color || '#94a3b8'}
                  weight={p.weight || 2}
                />
                <Line.Segment
                  point1={[xRange[0] + 0.2, yPos - 0.15]}
                  point2={[xRange[0], yPos]}
                  color={p.color || '#94a3b8'}
                  weight={p.weight || 2}
                />
                <Line.Segment
                  point1={[xRange[1] - 0.2, yPos + 0.15]}
                  point2={[xRange[1], yPos]}
                  color={p.color || '#94a3b8'}
                  weight={p.weight || 2}
                />
                <Line.Segment
                  point1={[xRange[1] - 0.2, yPos - 0.15]}
                  point2={[xRange[1], yPos]}
                  color={p.color || '#94a3b8'}
                  weight={p.weight || 2}
                />
                {/* Labeled points */}
                {points.map((pt, i) => (
                  <React.Fragment key={i}>
                    <Point
                      x={Number(pt.x)}
                      y={yPos}
                      color={pt.color || '#2563eb'}
                    />
                    {pt.label && (
                      <Text
                        x={Number(pt.x)}
                        y={yPos + (pt.labelOffset ?? 0.4)}
                        color={pt.labelColor || '#94a3b8'}
                        size={pt.labelSize || 14}
                        weight="bold"
                      >
                        {pt.label}
                      </Text>
                    )}
                  </React.Fragment>
                ))}
              </React.Fragment>
            );
          }

          // Line segment between two points
          if (t === 'segment') {
            const p1 = Array.isArray(p.point1) ? p.point1 : [0, 0];
            const p2 = Array.isArray(p.point2) ? p.point2 : [1, 1];
            return (
              <Line.Segment
                key={idx}
                point1={p1}
                point2={p2}
                color={p.color || '#2563eb'}
                weight={p.weight || 2}
              />
            );
          }

          // Vector (arrow)
          if (t === 'vector') {
            const tail = Array.isArray(p.tail) ? p.tail : [0, 0];
            const tip = Array.isArray(p.tip) ? p.tip : [1, 1];
            return (
              <Vector
                key={idx}
                tail={tail}
                tip={tip}
                color={p.color || '#dc2626'}
              />
            );
          }

          return null;
        })}
      </Mafs>
    </div>
  );
};

const renderTableImage = (imageComponent) => {
  if (!imageComponent || imageComponent.type !== 'table') return null;
  const props = imageComponent.props || {};
  const headers = Array.isArray(props.headers) ? props.headers : [];
  const rows = Array.isArray(props.rows) ? props.rows : [];

  return (
    <div className="qps-di-stimulus">
      <div className="qps-di-table-wrap">
        <table className="qps-di-table">
          {headers.length > 0 && (
            <thead>
              <tr>
                {headers.map((h, idx) => (
                  <th key={idx}>{renderMathText(h)}</th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {rows.map((row, rIdx) => (
              <tr key={rIdx}>
                {(Array.isArray(row) ? row : []).map((cell, cIdx) => (
                  <td key={cIdx}>{renderMathText(cell)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const loadStimuliForQuestions = async (questions, fallbackTopics = ['algebra']) => {
  const topics = [
    ...new Set(
      questions
        .map((question) => question.topic)
        .filter(Boolean)
        .concat(fallbackTopics)
    ),
  ];
  const topicsData = await loadMultipleTopics(
    topics.length > 0 ? topics : fallbackTopics
  );
  let allStimuli = {};
  Object.values(topicsData).forEach((topicData) => {
    if (topicData?.stimuli) {
      allStimuli = { ...allStimuli, ...topicData.stimuli };
    }
  });
  return allStimuli;
};

const renderStimulus = (stimulus) => {
  if (!stimulus) return null;
  if (stimulus.type === 'table') {
    return (
      <div className="qps-di-stimulus">
        <div className="qps-di-title">{stimulus.title}</div>
        {stimulus.description ? (
          <div className="qps-di-desc">{stimulus.description}</div>
        ) : null}
        <div className="qps-di-table-wrap">
          <table className="qps-di-table">
            <thead>
              <tr>
                {(stimulus.table?.columns || []).map((c, idx) => (
                  <th key={idx}>{renderMathText(c)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(stimulus.table?.rows || []).map((row, rIdx) => (
                <tr key={rIdx}>
                  {row.map((cell, cIdx) => (
                    <td key={cIdx}>{renderMathText(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  return null;
};

const DIFFICULTY_RANK = {
  easy: 0,
  medium: 1,
  hard: 2,
  very_hard: 3,
};

function formatDifficultyLabel(d) {
  if (!d) return '';
  if (d === 'very_hard') return 'Very hard';
  return d;
}

const getAdaptiveGreTotalTimeSeconds = (questionCount, pacingSection) => {
  const parsedCount = Number.parseInt(questionCount, 10);
  if (!Number.isFinite(parsedCount) || parsedCount <= 0) {
    return DEFAULT_TOTAL_TIME_MINUTES * 60;
  }

  const section =
    pacingSection === 'section2' || parsedCount > GRE_QUANT_SECTION_1.questions
      ? GRE_QUANT_SECTION_2
      : GRE_QUANT_SECTION_1;
  return Math.round(
    parsedCount * (section.totalTimeSeconds / section.questions)
  );
};

const shuffleInPlace = (arr) => {
  // Fisher–Yates
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const orderQuestionsRandomWithinIncreasingDifficulty = (qs) => {
  const groups = new Map();
  qs.forEach((q) => {
    const key = q?.difficulty || 'unknown';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(q);
  });

  const orderedKeys = Array.from(groups.keys()).sort((a, b) => {
    const ra = DIFFICULTY_RANK[a] ?? 99;
    const rb = DIFFICULTY_RANK[b] ?? 99;
    return ra - rb;
  });

  const out = [];
  orderedKeys.forEach((k) => {
    out.push(...shuffleInPlace([...groups.get(k)]));
  });
  return out;
};

const readSessionStorageJson = (key) => {
  try {
    const value = sessionStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch (error) {
    console.warn(`Failed to read ${key}:`, error);
    return null;
  }
};

const QuantPracticeSession = () => {
  const navigate = useNavigate();
  const authRedirectStartedRef = useRef(false);

  // Check if we're in review mode
  const urlParams = new URLSearchParams(window.location.search);
  const isReviewMode = urlParams.get('mode') === 'review';
  const reviewIndexParam = urlParams.get('index');
  const reviewStartIndex = Number.isFinite(
    Number.parseInt(reviewIndexParam, 10)
  )
    ? Number.parseInt(reviewIndexParam, 10)
    : null;

  // Session configuration from sessionStorage
  const [sessionConfig, setSessionConfig] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [stimuli, setStimuli] = useState({});
  const [loading, setLoading] = useState(true);

  // Session state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  const [showExplanation, setShowExplanation] = useState(false);
  const [sessionComplete, setSessionComplete] = useState(false);
  const feedbackMode = sessionConfig?.feedbackMode || 'immediate'; // 'immediate' | 'deferred'
  const isDeferredFeedback = feedbackMode === 'deferred';

  // Timing state
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [questionTimes, setQuestionTimes] = useState({});
  const [isPaused, setIsPaused] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const timerRef = useRef(null);

  // Exit dialog state
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [userEmail, setUserEmail] = useState(null);

  const redirectToLogin = (
    message = 'Votre session a expire. Veuillez vous reconnecter.'
  ) => {
    if (authRedirectStartedRef.current) {
      return;
    }

    authRedirectStartedRef.current = true;
    redirectToLoginWithReturn({
      navigate,
      redirectTo: window.location.pathname + window.location.search,
      message,
    });
  };

  // Load session configuration
  useEffect(() => {
    // Get user email
    try {
      const user = getStoredUser();
      const email = user?.email || localStorage.getItem('userEmail');
      if (email) {
        setUserEmail(email);
      }
    } catch (e) {
      console.error('Error getting user email:', e);
    }

    if (isReviewMode) {
      // Ensure no timers keep running while reviewing.
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setIsPaused(false);
      setTimeRemaining(null);
      setQuestionStartTime(null);

      // Load review session data
      const reviewData = sessionStorage.getItem('quantPracticeReview');
      if (reviewData) {
        const session = JSON.parse(reviewData);
        setSessionConfig(session.config);
        setQuestions(session.questions || []);
        setAnswers(session.answers || {});
        setQuestionTimes(session.questionTimes || {});
        setStimuli(session.stimuli || {});
        setFlaggedQuestions(new Set(session.flaggedQuestions || []));
        if (
          reviewStartIndex !== null &&
          reviewStartIndex >= 0 &&
          reviewStartIndex < (session.questions || []).length
        ) {
          setCurrentIndex(reviewStartIndex);
        } else {
          setCurrentIndex(0);
        }
        // In review mode, always show explanation & correct answer context
        setShowExplanation(true);
        setSessionComplete(false);
        setLoading(false);
      } else {
        navigate(-1);
      }
    } else {
      // Normal session mode
      const config = sessionStorage.getItem('quantPracticeSession');
      if (config) {
        const parsedConfig = JSON.parse(config);
        const activeSession = readSessionStorageJson(ACTIVE_SESSION_STORAGE_KEY);

        if (
          activeSession?.config?.id === parsedConfig.id &&
          Array.isArray(activeSession.questions) &&
          activeSession.questions.length > 0
        ) {
          const restoredIndex = Number.parseInt(activeSession.currentIndex, 10);
          const safeIndex =
            Number.isFinite(restoredIndex) &&
            restoredIndex >= 0 &&
            restoredIndex < activeSession.questions.length
              ? restoredIndex
              : 0;

          setSessionConfig(activeSession.config || parsedConfig);
          setQuestions(activeSession.questions);
          setStimuli(activeSession.stimuli || {});
          setAnswers(activeSession.answers || {});
          setQuestionTimes(activeSession.questionTimes || {});
          setCurrentIndex(safeIndex);
          setFlaggedQuestions(new Set(activeSession.flaggedQuestions || []));
          setShowExplanation(Boolean(activeSession.showExplanation));
          setSessionComplete(Boolean(activeSession.sessionComplete));
          setIsPaused(Boolean(activeSession.isPaused));
          setTimeRemaining(activeSession.timeRemaining ?? null);
          setSessionStartTime(activeSession.sessionStartTime || Date.now());
          setQuestionStartTime(activeSession.questionStartTime || Date.now());
          setLoading(false);
          return;
        }

        // Defensive reset: if the component is reused (e.g. navigating back/forward),
        // ensure we don't show the previous session's results state.
        setSessionComplete(false);
        setShowExplanation(false);
        setIsPaused(false);
        setTimeRemaining(null);
        setCurrentIndex(0);
        setFlaggedQuestions(new Set());
        setAnswers({});
        setQuestionTimes({});
        setSessionConfig(parsedConfig);
        loadQuestions(parsedConfig);
      } else {
        navigate(-1);
      }
    }
  }, [navigate, isReviewMode]);

  const saveCurrentSessionForReview = () => {
    try {
      sessionStorage.setItem(
        'quantPracticeReview',
        JSON.stringify({
          config: sessionConfig,
          questions,
          answers,
          questionTimes,
          stimuli,
          flaggedQuestions: Array.from(flaggedQuestions),
        })
      );
    } catch (e) {
      console.warn('Failed to save review session:', e);
    }
  };

  useEffect(() => {
    if (isReviewMode || loading || !sessionConfig?.id || questions.length === 0) {
      return;
    }

    try {
      sessionStorage.setItem(
        ACTIVE_SESSION_STORAGE_KEY,
        JSON.stringify({
          config: sessionConfig,
          questions,
          answers,
          questionTimes,
          stimuli,
          flaggedQuestions: Array.from(flaggedQuestions),
          currentIndex,
          showExplanation,
          sessionComplete,
          isPaused,
          timeRemaining,
          sessionStartTime,
          questionStartTime,
        })
      );
    } catch (error) {
      console.warn('Failed to persist active quant practice session:', error);
    }
  }, [
    answers,
    currentIndex,
    flaggedQuestions,
    isPaused,
    isReviewMode,
    loading,
    questionStartTime,
    questionTimes,
    questions,
    sessionComplete,
    sessionConfig,
    sessionStartTime,
    showExplanation,
    stimuli,
    timeRemaining,
  ]);

  // Load questions based on configuration (async for lazy loading)
  const loadQuestions = async (config) => {
    setLoading(true);

    try {
      // Clear any running timer from a previous session instance.
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      let finalQuestions = [];

      // Check if we're continuing an incomplete session
      if (
        config.questionPool === 'continue' &&
        config.savedQuestions?.length > 0
      ) {
        // Use saved questions from the incomplete session
        finalQuestions = config.savedQuestions;

        // Restore saved answers and times
        if (config.savedAnswers) {
          setAnswers(config.savedAnswers);
        }
        if (config.savedQuestionTimes) {
          setQuestionTimes(config.savedQuestionTimes);
        }

        if (config.savedStimuli && Object.keys(config.savedStimuli).length > 0) {
          setStimuli(config.savedStimuli);
        } else {
          const restoredStimuli = await loadStimuliForQuestions(
            finalQuestions,
            config.topics || ['algebra']
          );
          setStimuli(restoredStimuli);
        }

        // Find the first unanswered question to start from
        const firstUnanswered = finalQuestions.findIndex(
          (_, index) => config.savedAnswers?.[index] === undefined
        );
        if (firstUnanswered > 0) {
          setCurrentIndex(firstUnanswered);
        }
      } else {
        // Load fresh questions - lazy load only the needed topics
        let allStimuli = {};

        const topics = config.topics || ['algebra'];
        const topicsForStimuli = topics;

        // Lazy load topic exercises on demand
        const topicsData = await loadMultipleTopics(topicsForStimuli);

        topicsForStimuli.forEach((topicId) => {
          const topicData = topicsData[topicId];
          if (topicData?.exercises) {
            // Collect stimuli from this topic
            if (topicData.stimuli) {
              allStimuli = { ...allStimuli, ...topicData.stimuli };
            }
          }
        });

        // Store collected stimuli
        setStimuli(allStimuli);

        const questionPool = config.questionPool || 'all';
        const sessionUserEmail =
          userEmail ||
          getStoredUser()?.email ||
          localStorage.getItem('userEmail');
        const history = sessionUserEmail ? loadSessionHistory(sessionUserEmail) : [];
        const historySeenIds = sessionUserEmail
          ? getCombinedSeenQuestionIds(sessionUserEmail, history)
          : new Set();
        const configuredExcludeIds = Array.isArray(config.excludeQuestionIds)
          ? config.excludeQuestionIds
          : [];
        const excludeIds = [
          ...new Set([...configuredExcludeIds, ...historySeenIds]),
        ];

        if (questionPool === 'mistakes' && config.mistakeQuestionIds?.length) {
          finalQuestions = await getQuestionsFromTopics({
            topics,
            difficulty: 'mixed',
            questions: Math.min(
              config.questions || DEFAULT_QUESTION_COUNT,
              config.mistakeQuestionIds.length
            ),
            includeImages: config.includeImages,
            topicQuestions: config.topicQuestions,
            questionTypes: config.questionTypes,
            subconcepts: config.subconcepts,
            includeIds: config.mistakeQuestionIds,
          });
        } else {
          finalQuestions = await getQuestionsFromTopics({
            topics,
            difficulty: config.difficulty,
            questions: config.questions || DEFAULT_QUESTION_COUNT,
            includeImages: config.includeImages,
            topicQuestions: config.topicQuestions,
            questionTypes: config.questionTypes,
            subconcepts: config.subconcepts,
            excludeIds: questionPool === 'unseen' ? excludeIds : [],
            difficultyMix: config.difficultyMix,
          });
        }
      }

      // For revision-style sessions, keep difficulty increasing while staying random.
      // Don't reorder "continue" sessions so the user resumes in the same sequence.
      if (config.questionPool !== 'continue') {
        finalQuestions =
          orderQuestionsRandomWithinIncreasingDifficulty(finalQuestions);
      }

      setQuestions(finalQuestions);

      // Initialize timing
      if (config.timing === 'per_question') {
        const tpq = Number.parseInt(config.timePerQuestion, 10);
        setTimeRemaining(
          Number.isFinite(tpq) && tpq > 0
            ? tpq
            : DEFAULT_TIME_PER_QUESTION_SECONDS
        );
      } else if (config.timing === 'total') {
        const actualQuestionCount =
          finalQuestions.length || config.questions || DEFAULT_QUESTION_COUNT;
        const configuredSeconds = Number.parseInt(config.totalTimeSeconds, 10);
        const tt = Number.parseInt(config.totalTime, 10);
        const minutes =
          Number.isFinite(tt) && tt > 0 ? tt : DEFAULT_TOTAL_TIME_MINUTES;
        const shouldUseConfiguredSeconds =
          Number.isFinite(configuredSeconds) &&
          configuredSeconds > 0 &&
          Number.parseInt(config.totalTimeQuestionCount, 10) ===
            actualQuestionCount;
        setTimeRemaining(
          shouldUseConfiguredSeconds
            ? configuredSeconds
            : getAdaptiveGreTotalTimeSeconds(
                actualQuestionCount,
                config.grePacingSection
              ) || minutes * 60
        );
      }

      setSessionStartTime(Date.now());
      setQuestionStartTime(Date.now());
    } catch (error) {
      console.error('Error loading questions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Timer effect
  useEffect(() => {
    // In review mode, never run a countdown timer.
    if (isReviewMode) return;
    if (sessionConfig?.timing === 'untimed' || isPaused || sessionComplete) {
      return;
    }

    // Time isn't initialized yet (we set it after questions load)
    if (timeRemaining === null) return;

    if (timeRemaining <= 0) {
      if (sessionConfig?.timing === 'per_question') {
        handleTimeUp();
      } else if (sessionConfig?.timing === 'total') {
        finishSession();
      }
      return;
    }

    timerRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [timeRemaining, isPaused, sessionComplete, sessionConfig?.timing]);

  // Handle time up for per-question timing
  const handleTimeUp = () => {
    recordQuestionTime();

    if (currentIndex < questions.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      setTimeRemaining(
        sessionConfig?.timePerQuestion || DEFAULT_TIME_PER_QUESTION_SECONDS
      );
      setQuestionStartTime(Date.now());
      setShowExplanation(false);
    } else {
      finishSession();
    }
  };

  // Record time spent on current question
  const recordQuestionTime = () => {
    if (questionStartTime) {
      const timeSpent = Math.round((Date.now() - questionStartTime) / 1000);
      setQuestionTimes((prev) => ({
        ...prev,
        [currentIndex]: (prev[currentIndex] || 0) + timeSpent,
      }));
    }
  };

  // Handle answer selection
  const handleSingleChoiceSelect = (optionIndex) => {
    if (sessionComplete) return;

    setAnswers((prev) => ({
      ...prev,
      [currentIndex]: optionIndex,
    }));
  };

  const toggleMultiSelectOption = (optionIndex) => {
    if (sessionComplete) return;
    setAnswers((prev) => {
      const current = Array.isArray(prev[currentIndex])
        ? prev[currentIndex]
        : [];
      const set = new Set(current);
      if (set.has(optionIndex)) set.delete(optionIndex);
      else set.add(optionIndex);
      return { ...prev, [currentIndex]: Array.from(set).sort((a, b) => a - b) };
    });
  };

  const setNumericEntryAnswer = (value) => {
    if (sessionComplete) return;
    setAnswers((prev) => ({ ...prev, [currentIndex]: value }));
  };

  // Navigate to next question
  const handleNext = () => {
    if (isReviewMode) {
      // Review mode: never hide explanation or run timers.
      if (currentIndex < questions.length - 1) {
        setCurrentIndex((prev) => prev + 1);
        setShowExplanation(true);
        return;
      }
      navigate(-1);
      return;
    }

    recordQuestionTime();

    // Immediate mode: show explanation after answering
    if (
      !isReviewMode &&
      !isDeferredFeedback &&
      isAnswered &&
      !showExplanation
    ) {
      setShowExplanation(true);
      return;
    }

    // Deferred mode: never reveal explanation mid-session
    if (!isReviewMode && isDeferredFeedback) {
      if (!isAnswered) return;
    }

    if (currentIndex < questions.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      setShowExplanation(false);

      if (!isReviewMode) {
        setQuestionStartTime(Date.now());
        if (sessionConfig?.timing === 'per_question') {
          setTimeRemaining(
            sessionConfig?.timePerQuestion || DEFAULT_TIME_PER_QUESTION_SECONDS
          );
        }
      }
    } else if (!isReviewMode) {
      finishSession();
    } else {
      // Review mode, last question - go back
      navigate(-1);
    }
  };

  // Navigate to previous question
  const handlePrevious = () => {
    if (isReviewMode) {
      if (currentIndex > 0) {
        setCurrentIndex((prev) => prev - 1);
        setShowExplanation(true);
      }
      return;
    }

    if (currentIndex > 0) {
      if (!isReviewMode) {
        recordQuestionTime();
        setQuestionStartTime(Date.now());
      }
      setCurrentIndex((prev) => prev - 1);
      setShowExplanation(false);
    }
  };

  // Toggle flag on current question
  const toggleFlag = () => {
    setFlaggedQuestions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(currentIndex)) {
        newSet.delete(currentIndex);
      } else {
        newSet.add(currentIndex);
      }
      return newSet;
    });
  };

  // Save session to localStorage history and sync with backend
  const saveSessionToHistory = async (isComplete = true) => {
    if (!userEmail || questions.length === 0) return;

    const results = calculateResults();
    const sessionId = sessionConfig?.id || `session_${Date.now()}`;
    const sessionData = {
      id: sessionId,
      timestamp: sessionConfig?.createdAt || new Date().toISOString(),
      config: sessionConfig,
      stimuli,
      questions: questions.map((q) => ({
        id: q.id,
        topic: q.topic,
        stimulusId: q.stimulusId,
        question: q.question,
        options: q.options,
        correct: q.correct,
        correctAnswer: q.correctAnswer,
        acceptedAnswers: q.acceptedAnswers,
        questionType: q.questionType,
        explanation: q.explanation,
        difficulty: q.difficulty,
        subconcept: q.subconcept,
      })),
      answers: { ...answers },
      questionTimes: { ...questionTimes },
      results: {
        ...results,
        isComplete,
      },
      flaggedQuestions: Array.from(flaggedQuestions),
    };

    upsertSessionInHistory(userEmail, sessionData);

    const answeredIds = extractAnsweredQuestionIds(questions, answers);
    mergeLocalSeenQuestionIds(userEmail, answeredIds);

    // Sync with backend (non-blocking)
    try {
      await savePracticeSessionHistory(sessionData);
      if (answeredIds.length > 0) {
        await syncSeenQuestionIds(answeredIds);
      }
    } catch (error) {
      if (error?.message === 'Authentication failed') {
        redirectToLogin();
        return;
      }
      console.warn('Failed to sync session with backend:', error);
      // Session is already saved to localStorage, so this is non-critical
    }
  };

  // Finish the session
  const finishSession = async () => {
    recordQuestionTime();
    setSessionComplete(true);
    // Persist a snapshot for "review mode" navigation.
    saveCurrentSessionForReview();

    // Calculate results
    const results = calculateResults();

    // Save to localStorage history
    saveSessionToHistory(true);

    // Save to backend
    try {
      const user = getStoredUser();
      if (user?.email) {
        await savePracticeCompletion({
          lessonId: 'quant-practice-session',
          lessonName: 'Quant Practice Session',
          practiceMode: sessionConfig?.timing || 'untimed',
          totalQuestions: questions.length,
          correctAnswers: results.correct,
          totalTimeSeconds: Math.round((Date.now() - sessionStartTime) / 1000),
          sessionStartTime: new Date(sessionStartTime).toISOString(),
          sessionEndTime: new Date().toISOString(),
          questionsData: {
            topics: sessionConfig?.topics,
            subconcepts: sessionConfig?.subconcepts,
            difficulty: sessionConfig?.difficulty,
            questionTimes,
            answers,
          },
        });
        clearReadinessCache();
      }
    } catch (error) {
      if (error?.message === 'Authentication failed') {
        redirectToLogin();
        return;
      }
      console.error('Error saving practice completion:', error);
    }
  };

  // Handle exit with confirmation
  const handleExit = () => {
    if (isReviewMode) {
      navigate(-1);
      return;
    }
    // Show exit dialog if session has started
    if (Object.keys(answers).length > 0) {
      setShowExitDialog(true);
    } else {
      navigate(-1);
    }
  };

  // Exit without saving
  const handleExitWithoutSaving = () => {
    setShowExitDialog(false);
    navigate(-1);
  };

  // Exit and save progress
  const handleExitAndSave = () => {
    recordQuestionTime();
    saveSessionToHistory(false); // Save as incomplete
    setShowExitDialog(false);
    navigate(-1);
  };

  // Calculate session results
  const calculateResults = () => {
    let correct = 0;
    let incorrect = 0;
    let unanswered = 0;

    questions.forEach((question, index) => {
      const userAnswer = answers[index];
      if (!isGreQuantQuestionAnswered(question, userAnswer)) {
        unanswered++;
      } else if (isGreQuantAnswerCorrect(question, userAnswer)) {
        correct++;
      } else {
        incorrect++;
      }
    });

    const totalTime = Math.round((Date.now() - sessionStartTime) / 1000);
    const avgTimePerQuestion = Math.round(totalTime / questions.length);

    return {
      correct,
      incorrect,
      unanswered,
      total: questions.length,
      score: Math.round((correct / questions.length) * 100),
      totalTime,
      avgTimePerQuestion,
    };
  };

  // Format time display
  const formatTime = (seconds) => {
    if (seconds === null) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Current question
  const currentQuestion = questions[currentIndex];
  const currentAnswer = answers[currentIndex];
  const isAnswered = isGreQuantQuestionAnswered(currentQuestion, currentAnswer);
  const isCorrect =
    isAnswered && isGreQuantAnswerCorrect(currentQuestion, currentAnswer);

  // Results for completed session
  const results = useMemo(() => {
    if (!sessionComplete) return null;
    return calculateResults();
  }, [sessionComplete, questions, answers]);

  if (loading) {
    return (
      <div className="qps-loading">
        <div className="qps-loading-spinner" />
        <p>Loading practice session...</p>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="qps-empty">
        <FiAlertCircle size={48} />
        <h2>No Questions Available</h2>
        <p>
          {sessionConfig?.questionPool === 'unseen'
            ? 'You have already attempted all available questions for these filters. Try another topic, difficulty, or switch to "All questions".'
            : sessionConfig?.questionPool === 'mistakes'
              ? 'No mistake questions found in your session history.'
              : 'No questions found for the selected topics and difficulty.'}
        </p>
        <button onClick={() => navigate(-1)}>
          <FiArrowLeft /> Go Back
        </button>
      </div>
    );
  }

  // Results screen
  if (sessionComplete) {
    return (
      <div className="qps-results">
        <div className="qps-results-header">
          <FiAward className="qps-results-icon" />
          <h1>Practice Complete!</h1>
        </div>

        <div className="qps-results-summary">
          <div className="qps-results-score">
            <span className="qps-score-value">{results.score}%</span>
            <span className="qps-score-label">Score</span>
          </div>

          <div className="qps-results-stats">
            <div className="qps-stat correct">
              <FiCheck />
              <span>{results.correct}</span>
              <label>Correct</label>
            </div>
            <div className="qps-stat incorrect">
              <FiX />
              <span>{results.incorrect}</span>
              <label>Incorrect</label>
            </div>
            <div className="qps-stat unanswered">
              <FiMinus />
              <span>{results.unanswered}</span>
              <label>Skipped</label>
            </div>
          </div>

          <div className="qps-results-time">
            <FiClock />
            <span>Total time: {formatTime(results.totalTime)}</span>
            <span>
              Avg per question: {formatTime(results.avgTimePerQuestion)}
            </span>
          </div>
        </div>

        <div className="qps-results-review">
          <h3>Question Review</h3>
          <div className="qps-review-grid">
            {questions.map((question, index) => {
              const userAnswer = answers[index];
              const wasAnswered = isGreQuantQuestionAnswered(question, userAnswer);
              const isCorrect =
                wasAnswered && isGreQuantAnswerCorrect(question, userAnswer);

              return (
                <div
                  key={index}
                  className={`qps-review-item ${wasAnswered ? (isCorrect ? 'correct' : 'incorrect') : 'skipped'}`}
                  onClick={() => {
                    saveCurrentSessionForReview();
                    navigate(
                      `/exams/gre/quant-practice/session?mode=review&index=${index}`
                    );
                  }}
                >
                  <span className="qps-review-number">{index + 1}</span>
                  {wasAnswered ? (
                    isCorrect ? (
                      <FiCheck />
                    ) : (
                      <FiX />
                    )
                  ) : (
                    <FiMinus />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="qps-results-actions">
          <button className="qps-btn-secondary" onClick={() => navigate(-1)}>
            <FiArrowLeft /> Go Back
          </button>
          <button
            className="qps-btn-primary"
            onClick={() => {
              setSessionComplete(false);
              setCurrentIndex(0);
              setAnswers({});
              setFlaggedQuestions(new Set());
              setQuestionTimes({});
              setSessionStartTime(Date.now());
              setQuestionStartTime(Date.now());
              if (sessionConfig?.timing === 'per_question') {
                setTimeRemaining(
                  sessionConfig?.timePerQuestion ||
                    DEFAULT_TIME_PER_QUESTION_SECONDS
                );
              } else if (sessionConfig?.timing === 'total') {
                const configuredSeconds = Number.parseInt(
                  sessionConfig?.totalTimeSeconds,
                  10
                );
                setTimeRemaining(
                  Number.isFinite(configuredSeconds) && configuredSeconds > 0
                    ? configuredSeconds
                    : getAdaptiveGreTotalTimeSeconds(
                        questions.length || sessionConfig?.questions,
                        sessionConfig?.grePacingSection
                      )
                );
              }
            }}
          >
            <FiRefreshCw /> Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="qps-container">
      {/* Header */}
      <div className="qps-header">
        <button className="qps-back-btn" onClick={handleExit}>
          <FiArrowLeft /> Exit
        </button>

        <div className="qps-progress">
          <span>
            Question {currentIndex + 1} of {questions.length}
          </span>
          <div className="qps-progress-bar">
            <div
              className="qps-progress-fill"
              style={{
                width: `${((currentIndex + 1) / questions.length) * 100}%`,
              }}
            />
          </div>
        </div>

        {sessionConfig?.timing !== 'untimed' && (
          <div
            className={`qps-timer ${!isReviewMode && timeRemaining <= 30 ? 'warning' : ''}`}
          >
            <FiClock />
            {isReviewMode ? (
              <span>
                Time on this question:{' '}
                {formatTime(questionTimes?.[currentIndex] || 0)}
              </span>
            ) : (
              <span>{formatTime(timeRemaining)}</span>
            )}
            {!isReviewMode && (
              <button onClick={() => setIsPaused(!isPaused)}>
                {isPaused ? <FiPlay /> : <FiPause />}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Question Navigator */}
      <div className="qps-navigator">
        {questions.map((_, index) => (
          <button
            key={index}
            className={`qps-nav-btn ${index === currentIndex ? 'active' : ''} ${answers[index] !== undefined ? 'answered' : ''} ${flaggedQuestions.has(index) ? 'flagged' : ''}`}
            onClick={() => {
              if (!isReviewMode) recordQuestionTime();
              setCurrentIndex(index);
              setShowExplanation(isReviewMode);
              if (!isReviewMode) setQuestionStartTime(Date.now());
            }}
          >
            {index + 1}
            {flaggedQuestions.has(index) && (
              <FiFlag className="qps-flag-icon" />
            )}
          </button>
        ))}
      </div>

      {/* Question Content */}
      <div className="qps-content">
        <div className="qps-question-header">
          <span
            className="qps-difficulty"
            data-difficulty={currentQuestion?.difficulty}
          >
            {formatDifficultyLabel(currentQuestion?.difficulty)}
          </span>
          <span className="qps-topic">
            {currentQuestion?.subconcept?.replace(/_/g, ' ')}
          </span>
          <button
            className={`qps-flag-btn ${flaggedQuestions.has(currentIndex) ? 'flagged' : ''}`}
            onClick={toggleFlag}
          >
            <FiFlag /> {flaggedQuestions.has(currentIndex) ? 'Flagged' : 'Flag'}
          </button>
        </div>

        <div className="qps-question-text">
          {renderMathText(currentQuestion?.question)}
        </div>

        {/* Data Interpretation stimulus (shared) */}
        {currentQuestion?.questionType === 'data_interpretation' &&
          currentQuestion?.stimulusId &&
          renderStimulus(stimuli?.[currentQuestion.stimulusId])}

        {/* Quantitative Comparison quantities */}
        {currentQuestion?.questionType === 'quantitative_comparison' && (
          <div className="qps-qc">
            <div className="qps-qc-col">
              <div className="qps-qc-label">Quantity A</div>
              <div className="qps-qc-value">
                {renderMathText(currentQuestion?.quantityA)}
              </div>
            </div>
            <div className="qps-qc-col">
              <div className="qps-qc-label">Quantity B</div>
              <div className="qps-qc-value">
                {renderMathText(currentQuestion?.quantityB)}
              </div>
            </div>
          </div>
        )}

        {/* Image visualization */}
        {currentQuestion?.hasImage && currentQuestion?.imageComponent && (
          <div className="qps-question-image">
            <ChartErrorBoundary key={currentQuestion.id || currentIndex}>
              {renderMafsImage(currentQuestion.imageComponent) ||
                renderTableImage(currentQuestion.imageComponent) || (
                  <div className="qps-image-placeholder">
                    [Graph/Diagram could not be rendered]
                  </div>
                )}
            </ChartErrorBoundary>
          </div>
        )}

        {/* Answer UI */}
        {currentQuestion?.questionType === 'numeric_entry' ? (
          <div className="qps-numeric-entry">
            <label className="qps-numeric-label">Enter your answer:</label>
            <input
              className="qps-numeric-input"
              type="text"
              value={normalizeNumericEntry(currentAnswer)}
              onChange={(e) => setNumericEntryAnswer(e.target.value)}
              placeholder="e.g., 2.5 or 5/2"
              disabled={showExplanation || sessionComplete}
            />
            {showExplanation && (
              <div className="qps-numeric-feedback">
                <div>
                  <strong>Your answer:</strong>{' '}
                  {normalizeNumericEntry(currentAnswer) || '—'}
                </div>
                <div>
                  <strong>Correct answer:</strong>{' '}
                  {renderMathText(
                    Array.isArray(currentQuestion?.acceptedAnswers) &&
                      currentQuestion.acceptedAnswers.length > 0
                      ? String(currentQuestion.acceptedAnswers[0])
                      : String(currentQuestion?.correctAnswer ?? '')
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="qps-options">
            {(currentQuestion?.options || []).map((option, index) => {
              const type =
                currentQuestion?.questionType || 'multiple_choice_single';
              const multiSelected =
                Array.isArray(currentAnswer) && currentAnswer.includes(index);
              const singleSelected = currentAnswer === index;
              const isSelected =
                type === 'multiple_choice_multi'
                  ? multiSelected
                  : singleSelected;

              const isCorrectOption =
                type === 'multiple_choice_multi'
                  ? Array.isArray(currentQuestion.correct) &&
                    currentQuestion.correct.includes(index)
                  : index === currentQuestion.correct;

              const showCorrect = showExplanation && isCorrectOption;
              const showIncorrect =
                showExplanation && isSelected && !isCorrectOption;

              const label =
                type === 'quantitative_comparison' ? QC_OPTIONS[index] : option;

              return (
                <button
                  key={index}
                  className={`qps-option ${isSelected ? 'selected' : ''} ${showCorrect ? 'correct' : ''} ${showIncorrect ? 'incorrect' : ''}`}
                  onClick={() =>
                    type === 'multiple_choice_multi'
                      ? toggleMultiSelectOption(index)
                      : handleSingleChoiceSelect(index)
                  }
                  disabled={showExplanation || sessionComplete}
                >
                  <span className="qps-option-letter">
                    {String.fromCharCode(65 + index)}
                  </span>
                  <span className="qps-option-text">
                    {renderMathText(label)}
                  </span>
                  {showCorrect && <FiCheck className="qps-option-icon" />}
                  {showIncorrect && <FiX className="qps-option-icon" />}
                </button>
              );
            })}
          </div>
        )}

        {/* Review summary (always show in review mode) */}
        {isReviewMode && (
          <div className="qps-numeric-feedback" style={{ marginTop: 12 }}>
            <div>
              <strong>Time spent:</strong>{' '}
              {formatTime(questionTimes?.[currentIndex] || 0)}
            </div>
            <div>
              <strong>Your answer:</strong>{' '}
              {currentQuestion?.questionType === 'numeric_entry'
                ? normalizeNumericEntry(currentAnswer) || '—'
                : Array.isArray(currentAnswer)
                  ? currentAnswer.map(getOptionLetter).join(', ')
                  : getOptionLetter(currentAnswer)}
            </div>
            <div>
              <strong>Correct answer:</strong>{' '}
              {currentQuestion?.questionType === 'numeric_entry'
                ? renderMathText(
                    Array.isArray(currentQuestion?.acceptedAnswers) &&
                      currentQuestion.acceptedAnswers.length > 0
                      ? String(currentQuestion.acceptedAnswers[0])
                      : String(currentQuestion?.correctAnswer ?? '')
                  )
                : Array.isArray(currentQuestion?.correct)
                  ? currentQuestion.correct.map(getOptionLetter).join(', ')
                  : getOptionLetter(currentQuestion?.correct)}
            </div>
            <div>
              <strong>Result:</strong> {isCorrect ? 'Correct' : 'Incorrect'}
            </div>
          </div>
        )}

        {/* Explanation */}
        {showExplanation && (isReviewMode || !isDeferredFeedback) && (
          <div
            className={`qps-explanation ${isCorrect ? 'correct' : 'incorrect'}`}
          >
            <div className="qps-explanation-header">
              {isCorrect ? (
                <>
                  <FiCheck /> Correct!
                </>
              ) : (
                <>
                  <FiX /> Incorrect
                </>
              )}
            </div>
            <div className="qps-explanation-text">
              {renderMathText(currentQuestion?.explanation)}
            </div>
          </div>
        )}
      </div>

      {/* Footer Navigation */}
      <div className="qps-footer">
        <button
          className="qps-btn-secondary"
          onClick={handlePrevious}
          disabled={currentIndex === 0}
        >
          <FiArrowLeft /> Previous
        </button>

        <div className="qps-footer-info">
          {!isReviewMode && flaggedQuestions.size > 0 && (
            <span className="qps-flagged-count">
              <FiFlag /> {flaggedQuestions.size} flagged
            </span>
          )}
          {isReviewMode && (
            <span className="qps-review-label">
              <FiBookOpen /> Review Mode
            </span>
          )}
        </div>

        <button
          className="qps-btn-primary"
          onClick={handleNext}
          disabled={
            isReviewMode
              ? false
              : isDeferredFeedback
                ? !isAnswered
                : !isAnswered && !showExplanation
          }
        >
          {isReviewMode ? (
            currentIndex === questions.length - 1 ? (
              showExplanation ? (
                <>
                  Back to Results <FiHome />
                </>
              ) : (
                <>
                  Show Explanation <FiTarget />
                </>
              )
            ) : showExplanation ? (
              <>
                Next <FiArrowRight />
              </>
            ) : (
              <>
                Show Explanation <FiTarget />
              </>
            )
          ) : isDeferredFeedback ? (
            currentIndex === questions.length - 1 ? (
              <>
                Finish <FiCheck />
              </>
            ) : (
              <>
                Next <FiArrowRight />
              </>
            )
          ) : currentIndex === questions.length - 1 ? (
            showExplanation ? (
              <>
                Finish <FiCheck />
              </>
            ) : (
              <>
                Check Answer <FiTarget />
              </>
            )
          ) : showExplanation ? (
            <>
              Next <FiArrowRight />
            </>
          ) : (
            <>
              Check Answer <FiTarget />
            </>
          )}
        </button>
      </div>

      {/* Pause overlay */}
      {isPaused && (
        <div className="qps-pause-overlay">
          <div className="qps-pause-modal">
            <FiPause size={48} />
            <h2>Session Paused</h2>
            <p>Take a break. Your progress is saved.</p>
            <button onClick={() => setIsPaused(false)}>
              <FiPlay /> Resume
            </button>
          </div>
        </div>
      )}

      {/* Exit confirmation dialog */}
      {showExitDialog && (
        <div className="qps-pause-overlay">
          <div className="qps-exit-modal">
            <FiAlertCircle size={48} />
            <h2>Exit Session?</h2>
            <p>
              You've answered {Object.keys(answers).length} of{' '}
              {questions.length} questions. Would you like to save your
              progress?
            </p>
            <div className="qps-exit-actions">
              <button
                className="qps-btn-secondary"
                onClick={handleExitWithoutSaving}
              >
                <FiX /> Exit Without Saving
              </button>
              <button className="qps-btn-primary" onClick={handleExitAndSave}>
                <FiCheck /> Save & Exit
              </button>
            </div>
            <button
              className="qps-cancel-btn"
              onClick={() => setShowExitDialog(false)}
            >
              Continue Session
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Missing icon import
const FiMinus = ({ className }) => (
  <svg
    className={className}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

export default QuantPracticeSession;
